import { useState } from "react"

import { toast } from "sonner"

import Icon from "@/components/Icon"
import StatusDot from "@/components/incentives/StatusDot"
import { driversText, ukDateTimeText } from "@/components/incentives/pushText"
import { Button } from "@/components/ui/button"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Spinner } from "@/components/ui/spinner"
import {
  type Incentive,
  type IncentivePushSummary,
  type PushSendStatus,
  useIncentivePushDrivers,
  useIncentivePushes,
  useRetryPush,
} from "@/hooks/useIncentives"

const SEND_DOTS: Record<PushSendStatus, string> = {
  queued: "bg-indicators-yellow",
  sending: "bg-indicators-blue",
  sent: "bg-indicators-green",
  request_failed: "bg-indicators-red",
  delivered: "bg-indicators-green",
}

const SEND_LABELS: Record<PushSendStatus, string> = {
  queued: "Queued",
  sending: "Sending",
  sent: "Sent",
  request_failed: "Failed",
  delivered: "Delivered",
}

const HEAD_CELL =
  "whitespace-nowrap border-b border-palette-jungleGreen200 px-4 py-2 text-left text-xs font-normal text-palette-jungleGreen900"
const CELL = "px-4 py-2 text-sm"

const errorMessage = (error: unknown) => (error instanceof Error ? error.message : "Unknown error")

const RetryUnsentButton = ({
  incentive,
  push,
}: {
  incentive: Incentive
  push: IncentivePushSummary
}) => {
  const retry = useRetryPush()
  const live = incentive.status === "live"
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      disabled={!live || retry.isPending}
      title={live ? undefined : "A push can only be retried while its incentive is live"}
      onClick={() =>
        retry.mutate(
          { id: incentive.id, pushId: push.id },
          {
            onSuccess: ({ retrying }) =>
              toast.success(`Retrying the push to ${driversText(retrying)}`),
            onError: (error) =>
              toast.error("The push wasn't retried", { description: errorMessage(error) }),
          },
        )
      }
    >
      Retry unsent
    </Button>
  )
}

const PushDrivers = ({ incentiveId, pushId }: { incentiveId: string; pushId: string }) => {
  const drivers = useIncentivePushDrivers(incentiveId, pushId, true)
  if (drivers.isLoading) {
    return (
      <div className="flex justify-center py-4">
        <Spinner />
      </div>
    )
  }
  if (drivers.error) {
    return (
      <p className="px-4 py-3 text-sm text-palette-jungleGreen700">
        The drivers couldn't be loaded: {errorMessage(drivers.error)}
      </p>
    )
  }
  return (
    <div className="max-h-96 overflow-y-auto">
      <table className="w-full">
        <thead className="sticky top-0 bg-white">
          <tr>
            <th className={HEAD_CELL}>Driver</th>
            <th className={`${HEAD_CELL} w-32`}>Person ID</th>
            <th className={`${HEAD_CELL} w-32`}>Status</th>
            <th className={`${HEAD_CELL} w-40`}>Opened</th>
          </tr>
        </thead>
        <tbody>
          {drivers.data.map((driver) => (
            <tr key={driver.notification_id} className="border-b border-palette-jungleGreen100">
              <td className={`${CELL} text-palette-jungleGreen900`}>{driver.name || "-"}</td>
              <td className={`${CELL} tabular-nums text-palette-jungleGreen700`}>
                {driver.person_id ?? "-"}
              </td>
              <td className={CELL}>
                <StatusDot
                  className={SEND_DOTS[driver.status]}
                  label={SEND_LABELS[driver.status]}
                />
              </td>
              <td className={`${CELL} text-palette-jungleGreen700`}>
                {driver.opened_at ? ukDateTimeText(driver.opened_at) : "-"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const PushRow = ({ incentive, push }: { incentive: Incentive; push: IncentivePushSummary }) => {
  const [open, setOpen] = useState(false)
  const sentBy = push.sent_by_name ? ` by ${push.sent_by_name}` : ""

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="rounded-lg border border-palette-jungleGreen200"
    >
      <div className="flex flex-wrap items-start gap-4 p-4">
        <CollapsibleTrigger asChild>
          <button
            type="button"
            aria-label={`Sent ${ukDateTimeText(push.sent_at)}`}
            className="flex min-w-0 flex-1 items-start gap-3 text-left"
          >
            <Icon name={open ? "caretDown" : "caretRight"} className="mt-1 h-3 w-3 shrink-0" />
            <div className="min-w-0 space-y-1">
              <p className="text-xs text-palette-jungleGreen600">
                {`Sent ${ukDateTimeText(push.sent_at)}${sentBy}, to ${driversText(push.driver_count)}`}
              </p>
              <p className="text-sm font-semibold text-palette-jungleGreen900">{push.title}</p>
              <p className="text-sm text-palette-jungleGreen700">{push.body}</p>
              <div className="flex flex-wrap gap-x-4 gap-y-1 pt-1 text-sm">
                <StatusDot className={SEND_DOTS.sent} label={`${push.sent} sent`} />
                <StatusDot className={SEND_DOTS.request_failed} label={`${push.failed} failed`} />
                <StatusDot className={SEND_DOTS.sending} label={`${push.sending} sending`} />
                <StatusDot className={SEND_DOTS.queued} label={`${push.queued} queued`} />
                {push.stuck > 0 && (
                  <StatusDot className={SEND_DOTS.request_failed} label={`${push.stuck} stuck`} />
                )}
                <span className="text-palette-jungleGreen900">{`${push.opened} opened`}</span>
              </div>
            </div>
          </button>
        </CollapsibleTrigger>
        {/* Safe while a send is still running, since a retry leaves rows that are sending and not yet stuck */}
        {push.failed + push.queued + push.stuck > 0 && (
          <RetryUnsentButton incentive={incentive} push={push} />
        )}
      </div>
      <CollapsibleContent className="border-t border-palette-jungleGreen200">
        <PushDrivers incentiveId={incentive.id} pushId={push.id} />
      </CollapsibleContent>
    </Collapsible>
  )
}

export default function PushLog({ incentive }: { incentive: Incentive }) {
  const pushes = useIncentivePushes(incentive.id)

  if (pushes.isLoading) {
    return (
      <div className="flex justify-center py-4">
        <Spinner />
      </div>
    )
  }
  if (pushes.error) {
    return (
      <p className="text-sm text-palette-jungleGreen700">
        The pushes couldn't be loaded: {errorMessage(pushes.error)}
      </p>
    )
  }
  if (pushes.data.length === 0) {
    return <p className="text-sm text-palette-jungleGreen700">No pushes have been sent yet.</p>
  }
  return (
    <div className="space-y-3">
      <p className="text-xs leading-relaxed text-palette-jungleGreen600">
        Sent means Dispatch accepted it, not that it reached the phone. A driver with no logged-in
        device or with notifications off still shows as sent.
      </p>
      {pushes.data.map((push) => (
        <PushRow key={push.id} incentive={incentive} push={push} />
      ))}
    </div>
  )
}
